import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

interface LogEntry {
  id: string;
  method: string;
  path: string;
  status_code: number | null;
  user_id: string | null;
  username: string | null;
  ip_address: string | null;
  user_agent: string | null;
  response_time_ms: number | null;
  error_message: string | null;
  created_at: string;
}

interface LogsResponse {
  items: LogEntry[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export default function AdminLogs() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    method: '',
    status_code: '',
    ip_address: '',
  });
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'logs', page, search, filters],
    queryFn: async () => {
      const params: Record<string, any> = { page, per_page: 50 };
      if (search) params.search = search;
      if (filters.method) params.method = filters.method;
      if (filters.status_code) params.status_code = parseInt(filters.status_code);
      if (filters.ip_address) params.ip_address = filters.ip_address;
      const response = await api.get('/admin/logs', { params });
      return response.data as LogsResponse;
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  const clearLogsMutation = useMutation({
    mutationFn: async (days: number) => {
      await api.delete('/admin/logs', { params: { days } });
    },
    onSuccess: (_, days) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'logs'] });
      toast.success(`Logs older than ${days} days cleared`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to clear logs');
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const applyFilters = () => {
    setPage(1);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ method: '', status_code: '', ip_address: '' });
    setSearch('');
    setSearchInput('');
    setPage(1);
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'bg-blue-500/20 text-blue-400',
      POST: 'bg-green-500/20 text-green-400',
      PUT: 'bg-amber-500/20 text-amber-400',
      PATCH: 'bg-purple-500/20 text-purple-400',
      DELETE: 'bg-red-500/20 text-red-400',
      OPTIONS: 'bg-surface-600 text-surface-400',
    };
    return colors[method] || 'bg-surface-600 text-surface-400';
  };

  const getStatusColor = (status: number | null) => {
    if (!status) return 'text-surface-400';
    if (status >= 500) return 'text-red-400';
    if (status >= 400) return 'text-amber-400';
    if (status >= 300) return 'text-blue-400';
    return 'text-emerald-400';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">System Logs</h1>
          <p className="text-surface-400 mt-1">
            {data?.total || 0} total log entries
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="px-3 py-2 rounded-lg bg-surface-700 hover:bg-surface-600 text-white transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={() => {
              if (confirm('Clear logs older than 30 days?')) {
                clearLogsMutation.mutate(30);
              }
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
          >
            <TrashIcon className="w-4 h-4" />
            Clear Old
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by path, username, or IP..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-surface-800 border border-surface-600 text-white placeholder-surface-400 focus:outline-none focus:border-amber-500"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 rounded-lg bg-surface-700 hover:bg-surface-600 text-white transition-colors"
          >
            Search
          </button>
        </form>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            showFilters || filters.method || filters.status_code || filters.ip_address
              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
              : 'bg-surface-700 text-white hover:bg-surface-600'
          }`}
        >
          <FunnelIcon className="w-4 h-4" />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="p-4 rounded-xl bg-surface-800/50 border border-surface-700"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">Method</label>
              <select
                value={filters.method}
                onChange={(e) => setFilters({ ...filters, method: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-surface-700 border border-surface-600 text-white focus:outline-none focus:border-amber-500"
              >
                <option value="">All Methods</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">Status Code</label>
              <input
                type="number"
                value={filters.status_code}
                onChange={(e) => setFilters({ ...filters, status_code: e.target.value })}
                placeholder="e.g. 200, 404, 500"
                className="w-full px-3 py-2 rounded-lg bg-surface-700 border border-surface-600 text-white focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">IP Address</label>
              <input
                type="text"
                value={filters.ip_address}
                onChange={(e) => setFilters({ ...filters, ip_address: e.target.value })}
                placeholder="e.g. 192.168.1.1"
                className="w-full px-3 py-2 rounded-lg bg-surface-700 border border-surface-600 text-white focus:outline-none focus:border-amber-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={clearFilters}
              className="px-4 py-2 rounded-lg bg-surface-700 hover:bg-surface-600 text-white transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-medium transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </motion.div>
      )}

      {/* Logs Table */}
      <div className="bg-surface-800/50 rounded-xl border border-surface-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Time</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Method</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Path</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">User</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">IP</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-300">Time (ms)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-700 font-mono text-sm">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-surface-400">
                    No logs found
                  </td>
                </tr>
              ) : (
                data?.items.map((log) => (
                  <motion.tr
                    key={log.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => setSelectedLog(log)}
                    className="hover:bg-surface-700/30 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-2.5 text-surface-400 whitespace-nowrap">
                      {formatTime(log.created_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getMethodColor(log.method)}`}>
                        {log.method}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-surface-300 max-w-[300px] truncate">
                      {log.path}
                    </td>
                    <td className={`px-4 py-2.5 font-medium ${getStatusColor(log.status_code)}`}>
                      {log.status_code || '-'}
                    </td>
                    <td className="px-4 py-2.5 text-surface-300">
                      {log.username || <span className="text-surface-500">anonymous</span>}
                    </td>
                    <td className="px-4 py-2.5 text-surface-400">
                      {log.ip_address || '-'}
                    </td>
                    <td className="px-4 py-2.5 text-right text-surface-400">
                      {log.response_time_ms ?? '-'}
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-surface-700">
            <p className="text-sm text-surface-400">
              Page {data.page} of {data.pages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={page === data.pages}
                className="p-2 rounded-lg hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Log Detail Modal */}
      {selectedLog && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => setSelectedLog(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-surface-800 rounded-xl p-6 max-w-2xl w-full border border-surface-700 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Log Details</h2>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-1 rounded-lg hover:bg-surface-700 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-surface-400" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded text-sm font-medium ${getMethodColor(selectedLog.method)}`}>
                  {selectedLog.method}
                </span>
                <span className={`font-medium ${getStatusColor(selectedLog.status_code)}`}>
                  {selectedLog.status_code}
                </span>
                <span className="text-surface-400 flex items-center gap-1">
                  <ClockIcon className="w-4 h-4" />
                  {selectedLog.response_time_ms}ms
                </span>
              </div>
              <div className="p-3 rounded-lg bg-surface-700/50 font-mono text-sm text-surface-300 break-all">
                {selectedLog.path}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-surface-500 mb-1">Timestamp</label>
                  <p className="text-surface-300">{formatTime(selectedLog.created_at)}</p>
                </div>
                <div>
                  <label className="block text-xs text-surface-500 mb-1">User</label>
                  <p className="text-surface-300">{selectedLog.username || 'Anonymous'}</p>
                </div>
                <div>
                  <label className="block text-xs text-surface-500 mb-1">IP Address</label>
                  <p className="text-surface-300">{selectedLog.ip_address || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs text-surface-500 mb-1">User ID</label>
                  <p className="text-surface-300 text-xs font-mono">{selectedLog.user_id || '-'}</p>
                </div>
              </div>
              {selectedLog.user_agent && (
                <div>
                  <label className="block text-xs text-surface-500 mb-1">User Agent</label>
                  <p className="text-surface-400 text-xs break-all">{selectedLog.user_agent}</p>
                </div>
              )}
              {selectedLog.error_message && (
                <div>
                  <label className="block text-xs text-red-400 mb-1">Error</label>
                  <p className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm font-mono break-all">
                    {selectedLog.error_message}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}

